import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface OwnerNotification {
  id: string;
  owner_id: string;
  property_id: string;
  cleaning_task_id: string | null;
  maintenance_issue_id: string | null;
  maintenance_issue_report_id?: string | null;
  inventory_reorder_id?: string | null;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export function useOwnerNotifications(limit?: number) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["owner-notifications", user?.id, limit],
    queryFn: async () => {
      if (!user?.id) return [];

      let query = supabase
        .from("owner_notifications")
        .select("*")
        .eq("owner_id", user.id)
        .order("created_at", { ascending: false });

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as OwnerNotification[];
    },
    enabled: !!user?.id,
  });
}

export function useUnreadNotificationsCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["owner-notifications-unread-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from("owner_notifications")
        .select("*", { count: "exact", head: true })
        .eq("owner_id", user.id)
        .eq("is_read", false);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("owner_notifications")
        .update({ is_read: true })
        .eq("id", notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owner-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["owner-notifications-unread-count"] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) return;

      const { error } = await supabase
        .from("owner_notifications")
        .update({ is_read: true })
        .eq("owner_id", user.id)
        .eq("is_read", false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owner-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["owner-notifications-unread-count"] });
    },
  });
}
